import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DialogModule} from 'primeng/dialog';
import {DialogService} from 'primeng/dynamicdialog';
import {Subject} from 'rxjs';
import {filter, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {FieldAgroOperation} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {ServerTranslateService} from '@services/server-translate.service';
import {FieldDetailsTimelineSectionComponent} from '../field-details-timeline-section/field-details-timeline-section.component';
import {dateToYmd} from '../../managers/field-details-date.util';
import {
  AtmCalendarRow,
  AtmCalendarViewRow,
  filterAtmByYmd,
  toAtmCalendarViewRows,
  uniqueAtmTimelineDates,
} from '../../managers/field-details-atm-tab.util';
import {FieldDetailsAtmTabService} from '../../managers/field-details-atm-tab.service';
import {AgroOperation} from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-parent/passport-menu-parent';
import {ModalAgrotechnicalEditComponent} from '@app/pages/electronic-field-map/pages/fields/passport/menu/passport-menu-agrotechnical/modal-agrotechnical-edit/modal-agrotechnical-edit.component';
import {ModalFormDelete} from '@components/modal-form-delete/modal-form-delete';
import {TableActionsComponent} from '@components/table-actions/table-actions.component';

@Component({
  selector: 'app-field-details-atm-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DialogModule,
    FormBuilder,
    SecondaryButton,
    PrimaryButton,
    DataTableComponent,
    ServerTranslatePipe,
    FieldDetailsTimelineSectionComponent,
    TableActionsComponent,
  ],
  providers: [DialogService],
  templateUrl: './field-details-atm-tab.component.html',
  styleUrls: ['./field-details-atm-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldDetailsAtmTabComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('nameTemplate') nameTemplate!: TemplateRef<unknown>;
  @ViewChild('sourceTemplate') sourceTemplate!: TemplateRef<unknown>;
  @ViewChild('actionsTemplate') actionsTemplate!: TemplateRef<unknown>;

  private readonly atmTabService = inject(FieldDetailsAtmTabService);
  private readonly translate = inject(TranslateService);
  private readonly serverTranslate = inject(ServerTranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialogService = inject(DialogService);
  private readonly destroy$ = new Subject<void>();

  @Input({required: true}) fieldId!: string;
  @Input() crops: {id?: string}[] = [];
  /** Later: hide Add without removing the flow. */
  @Input() showAdd = true;

  readonly timelineDatesLoading = signal(false);
  readonly loadFailed = signal(false);
  readonly hasSeason = signal(false);
  readonly rows = signal<AtmCalendarViewRow[]>([]);
  readonly timelineDates = signal<Date[]>([]);
  readonly selectedTimelineDate = signal<Date | null>(null);
  readonly dayRows = computed(() => {
    const selected = this.selectedTimelineDate();
    const ymd = selected ? dateToYmd(selected) : null;
    return filterAtmByYmd(this.rows(), ymd);
  });
  readonly emptyAnomalies = new Set<string>();

  tableColumns: TableColumn[] = [];
  readonly tableConfig: TableConfig = {
    selectable: false,
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [10, 25, 50],
    loading: false,
    emptyMessage: 'No data found',
    keepOrder: true,
  };

  calendarDialogVisible = false;
  calendarFormFields: IMsfFormBuilder[] = [];
  calendarFormModel: {date?: Date | string | null} = {};

  private rawRows: AtmCalendarRow[] = [];

  ngOnInit(): void {
    if (this.fieldId) {
      this.loadOperations();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fieldId'] && !changes['fieldId'].firstChange && this.fieldId) {
      this.loadOperations();
      return;
    }
    if (changes['crops'] && this.rawRows.length) {
      this.rebuildViewRows();
    }
  }

  ngAfterViewInit(): void {
    this.tableColumns = [
      {field: 'originalDate', header: 'Date', type: 'date'},
      {field: 'cropLabel', header: 'Crop', type: 'text'},
      {
        field: 'nameKey',
        header: 'Name2',
        type: 'custom',
        customTemplate: this.nameTemplate,
      },
      {field: 'comment', header: 'Comment', type: 'text'},
      {
        field: 'sourceKey',
        header: 'Source',
        type: 'custom',
        customTemplate: this.sourceTemplate,
      },
      {
        field: '',
        header: 'Action',
        type: 'custom',
        width: '100px',
        customTemplate: this.actionsTemplate,
      },
    ];
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTimelinePick(d: Date): void {
    this.selectedTimelineDate.set(d);
  }

  openCalendarDialog(): void {
    this.rebuildCalendarFormFields();
    this.calendarFormModel = {date: this.selectedTimelineDate() ?? this.startOfToday()};
    this.calendarDialogVisible = true;
    this.cdr.markForCheck();
  }

  closeCalendarDialog(): void {
    this.calendarDialogVisible = false;
    this.cdr.markForCheck();
  }

  applyCalendarSelection(): void {
    const raw = this.calendarFormModel?.date ?? null;
    if (raw == null || raw === '') {
      return;
    }
    const d = raw instanceof Date ? new Date(raw.getTime()) : new Date(String(raw));
    if (isNaN(d.getTime())) {
      return;
    }
    d.setHours(0, 0, 0, 0);
    const ymd = dateToYmd(d);
    const match = this.timelineDates().find(x => dateToYmd(x) === ymd);
    if (match) {
      this.onTimelinePick(match);
      this.closeCalendarDialog();
    }
  }

  openEditModal(item: FieldAgroOperation = {}): void {
    const ref = this.dialogService.open(ModalAgrotechnicalEditComponent, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('Agrotechnical measures'),
      width: '30rem',
      closable: true,
      modal: true,
      data: {
        model: item,
        fieldId: this.fieldId,
        operationTypeFullName: AgroOperation.AgroOperation,
        crops: this.crops,
      },
    });
    ref.onClose.pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result?.success) {
        this.loadOperations();
      }
    });
  }

  deleteItem(item: FieldAgroOperation): void {
    const ref = this.dialogService.open(ModalFormDelete, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('Delete Confirmation'),
      width: '30rem',
      closable: true,
      modal: true,
      data: {
        message: this.translate.instant('Are you sure you want to delete this item ?'),
      },
    });
    ref.onClose.pipe(
      takeUntil(this.destroy$),
      filter((confirmed): confirmed is true => confirmed === true && !!item.id),
      switchMap(() => this.atmTabService.deleteOperation(item.id!)),
    ).subscribe(() => this.loadOperations());
  }

  private loadOperations(): void {
    this.timelineDatesLoading.set(true);
    this.loadFailed.set(false);
    this.rawRows = [];
    this.rows.set([]);
    this.timelineDates.set([]);
    this.selectedTimelineDate.set(null);
    this.hasSeason.set(false);
    this.atmTabService
      .loadCalendar(this.fieldId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.timelineDatesLoading.set(false);
        }),
      )
      .subscribe(result => {
        this.loadFailed.set(!!result.failed);
        this.hasSeason.set(result.hasSeason);
        this.rawRows = result.rows;
        this.rebuildViewRows();
        const dates = uniqueAtmTimelineDates(this.rawRows);
        this.timelineDates.set(dates);
        this.selectedTimelineDate.set(dates.length ? dates[dates.length - 1] : null);
      });
  }

  private rebuildViewRows(): void {
    this.rows.set(toAtmCalendarViewRows(this.rawRows, cropId => this.cropLabelFor(cropId)));
  }

  private cropLabelFor(cropId: string | null): string {
    if (!cropId) {
      return '—';
    }
    const crop = this.crops.find(c => c.id === cropId);
    if (!crop) {
      return '—';
    }
    return this.serverTranslate.getDisplayValue(crop, 'names') || '—';
  }

  private rebuildCalendarFormFields(): void {
    const dates = this.timelineDates();
    const restrict = dates.length > 0;
    this.calendarFormFields = [
      {
        name: 'date',
        title: this.translate.instant('Select date'),
        type: 'date',
        required: false,
        options: {
          date: {
            dateOnly: true,
            allowOnlySelectedDate: restrict,
            ...(restrict
              ? {selectableDates: (): string[] => this.timelineDates().map(d => dateToYmd(d))}
              : {}),
          },
        },
      },
    ];
  }

  private startOfToday(): Date {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }
}
